import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '@/shared/store'
import { currentLayerAdapter, originalLayerAdapter } from './layerAdapters'
import type { LocalLayer } from './types'

// Base selectors for adapter state
const selectCurrentState = (state: RootState) => state.editor.layers.current
const selectOriginalState = (state: RootState) => state.editor.layers.original

const currentSelectors = currentLayerAdapter.getSelectors(selectCurrentState)
const originalSelectors = originalLayerAdapter.getSelectors(selectOriginalState)

/** Select a single layer by ID from current state — O(1) */
export const selectCurrentLayerById = currentSelectors.selectById

/** Select all current layers */
export const selectAllCurrentLayers = currentSelectors.selectAll

/** Select all original layers */
export const selectAllOriginalLayers = originalSelectors.selectAll

/** Select original layer by ID — O(1) */
export const selectOriginalLayerById = originalSelectors.selectById

/** Select current layers for a specific slide, sorted by z_index */
export const selectCurrentLayersBySlideId = createSelector(
  [selectAllCurrentLayers, (_state: RootState, slideId: number) => slideId],
  (layers, slideId): LocalLayer[] =>
    layers
      .filter((l) => l.slideId === slideId)
      .sort((a, b) => (a.z_index ?? 0) - (b.z_index ?? 0)),
)

/** Select text layers for a slide, sorted by z_index */
export const selectTextLayersBySlideId = createSelector(
  [selectCurrentLayersBySlideId],
  (layers): LocalLayer[] =>
    layers.filter((l) => l.layer_type === 'text'),
)

/** Select non-background image layers for a slide, sorted by z_index */
export const selectImageLayersBySlideId = createSelector(
  [selectCurrentLayersBySlideId],
  (layers): LocalLayer[] =>
    layers.filter((l) => l.layer_type === 'image' && l.image_asset_type !== 'background'),
)

/** Select the background layer for a slide, or null */
export const selectBackgroundLayerBySlideId = createSelector(
  [selectCurrentLayersBySlideId],
  (layers): LocalLayer | null =>
    layers.find(
      (l) => l.layer_type === 'image' && l.image_asset_type === 'background',
    ) ?? null,
)

/** Select original layers for a specific slide, sorted by z_index */
export const selectOriginalLayersBySlideId = createSelector(
  [selectAllOriginalLayers, (_state: RootState, slideId: number) => slideId],
  (layers, slideId): LocalLayer[] =>
    layers
      .filter((l) => l.slideId === slideId)
      .sort((a, b) => (a.z_index ?? 0) - (b.z_index ?? 0)),
)
